import { ReactNode, useEffect, useRef, useState, useCallback } from 'react';

import {
  addMouseZoomListener,
  addNodeDblClickListener,
  canZoom,
  canZoomIn,
  canZoomOut,
  center,
  downloadFullImage,
  downloadImage,
  fitScreen,
  focus,
  get,
  getEdges,
  getNodeEdgesData,
  getNodes,
  getSelectedEdgesData,
  getSelectedNodesData,
  getZoom,
  getZoomPercentage,
  goToNode,
  relayout,
  remove,
  select,
  unselect,
  update,
  updateItemState,
  zoomTo,
} from '../../utils';
import { UnknownFunction, Graph } from '../../types';
import { withEditorContext, EditorContextProps } from '../EditorContext';
import { UseFlowChartContextProps } from './useFlowChart.types';
import UseFlowChartContext from './UseFlowChartContext';

interface UseFlowChartIntegratorProps extends EditorContextProps {
  /* The content of the Utility Integrator */
  children: ReactNode;
}

export const UseFlowChartIntegratorCmp = (props: UseFlowChartIntegratorProps) => {
  const { children, executeCommand, graph } = props;
  const listenerUnbindFunctions = useRef<UnknownFunction[]>([]);

  useEffect(() => {
    // Cleans up any listeners on destroy to avoid performance issues
    return () => listenerUnbindFunctions.current.forEach((fn) => fn());
  }, []);

  /**
   * Saves a reference to the unbinding function for cleanup
   */
  const handleAddListener = (unbindFn: UnknownFunction): (() => void) => {
    listenerUnbindFunctions.current.push(unbindFn);

    return unbindFn;
  };

  /**
   * Gets methods returned by the `useFlowChart` hook
   */
  const getUtils = useCallback((): UseFlowChartContextProps => {
    return {
      // Edit
      add: ({ data, ...args }) => executeCommand('add', { model: data, ...args }),
      copy: () => executeCommand('copy'),
      get: (id) => get(graph as Graph, id),
      paste: () => executeCommand('paste'),
      relayout: () => relayout(graph as Graph),
      remove: (id) => remove(graph as Graph, id),
      removeSelected: () => executeCommand('remove'),
      select: (id) => select(graph as Graph, id),
      unselect: (id) => unselect(graph as Graph, id),
      setState: (item, state, value) => updateItemState(graph as Graph, item, state, value),
      update: (data) => update(graph as Graph, data),
      // Orientation
      canZoomIn: () => canZoomIn(graph as Graph),
      canZoomOut: () => canZoomOut(graph as Graph),
      center: () => center(graph as Graph),
      fitView: () => fitScreen(graph as Graph),
      focus: (id, animate, animateCfg) => focus(graph as Graph, id, animate, animateCfg),
      getZoom: () => getZoom(graph as Graph),
      getZoomPercentage: () => getZoomPercentage(graph as Graph),
      goToNode: (id) => goToNode(graph as Graph, id),
      isZoomEnabled: () => canZoom(graph as Graph),
      zoomIn: () => executeCommand('zoomIn'),
      zoomOut: () => executeCommand('zoomOut'),
      zoomTo: (n) => zoomTo(graph as Graph, n),
      // Download
      downloadFullImage: () => downloadFullImage(graph as Graph),
      downloadImage: () => downloadImage(graph as Graph),
      // Helpers
      getEdges: () => getEdges(graph as Graph),
      getNodes: () => getNodes(graph as Graph),
      getNodeEdges: (id) => getNodeEdgesData(graph as Graph, id),
      getSelectedNodes: () => getSelectedNodesData(graph as Graph),
      getSelectedEdges: () => getSelectedEdgesData(graph as Graph),
      // Listeners
      addMouseZoomListener: (fn) => handleAddListener(addMouseZoomListener(graph as Graph, fn)),
      addNodeDblClickListener: (fn) =>
        handleAddListener(addNodeDblClickListener(graph as Graph, fn)),
      ...props,
    };
  }, []);

  /**
   * To avoid unnecessary rerenders, this is put in state
   * See: https://reactjs.org/docs/context.html#caveats
   */
  const [utils] = useState(getUtils());

  return <UseFlowChartContext.Provider value={utils}>{children}</UseFlowChartContext.Provider>;
};

export const UseFlowChartIntegrator = withEditorContext(UseFlowChartIntegratorCmp);

export default UseFlowChartIntegrator;
